<template>
	<div class="min-h-screen flex" :class="isMobile ? 'flex-col' : ''">
		<div v-if="isMobile">
			<div class="flex justify-between items-center container py-2">
				<RouterLink :to="homeLink">
					<img class="h-6" src="/img/logo.svg" alt="Lix" />
					<h1 class="sr-only">Lix</h1>
				</RouterLink>
				<MobileMenuDropdown />
			</div>
		</div>
		<div v-else>
			<div class="sticky lg:w-56 top-0 left-0 py-3 h-[calc(100vh)]">
				<aside
					role="navigation"
					aria-label="Main menu"
					class="border-r-2 border-gray-200 dark:border-purple-400/30 flex flex-col items-start space-y-5 px-3.5 h-full"
				>
					<RouterLink :to="homeLink" class="lg:mb-5 w-full">
						<div
							class="icons-container icon-base flex justify-center items-center rounded-2xl"
						>
							<img class="h-10 lg:h-14" src="/img/logo.svg" alt="Lix" />
							<SearchIcon
								class="icon-blue-soft size-7 lg:size-10 mt-1.5 lg:mt-2 search-icon"
							/>
						</div>
						<h1 class="sr-only">Lix</h1>
					</RouterLink>
					<nav
						v-if="isLg"
						role="menu"
						aria-label="User menu"
						class="flex flex-col space-y-2.5 mt-6 w-full"
					>
						<RouterLink
							v-for="item in menuItems"
							:key="item.label"
							:to="item.to"
							class="flex items-center justify-start side-menu-item mx-0"
							active-class="side-menu-button-selected"
							exact-active-class="side-menu-item-selected"
						>
							<component
								:is="item.icon"
								class="size-7"
								:class="item.class"
								aria-hidden="true"
							/>
							<span>
								{{ item.label }}
							</span>
						</RouterLink>
						<button
							type="button"
							class="flex items-center justify-start side-menu-item"
							@click="confirmOpen = true"
						>
							<LogOutIcon
								class="size-7 icon-red-soft dark:fill-transparent fill-transparent"
								aria-hidden="true"
							/>
							<span>LOG UD</span>
						</button>
					</nav>
					<nav
						v-else
						role="menu"
						aria-label="User menu"
						class="flex flex-col items-center space-y-2.5 mt-6 w-full"
					>
						<AppTooltip
							v-for="item in menuItems"
							:key="item.label"
							type="action"
							:tooltip="item.label"
						>
							<RouterLink
								:to="item.to"
								class="flex items-center justify-center side-menu-item"
								active-class="side-menu-button-selected"
								exact-active-class="side-menu-item-selected"
							>
								<component
									:is="item.icon"
									class="size-7"
									:class="item.class"
									aria-hidden="true"
								/>
							</RouterLink>
						</AppTooltip>
						<AppTooltip
							type="action"
							class="flex items-center justify-center"
							tooltip="LOG UD"
						>
							<button
								type="button"
								class="side-menu-item"
								@click="confirmOpen = true"
							>
								<LogOutIcon
									class="size-7 icon-red-soft dark:fill-transparent fill-transparent"
									aria-hidden="true"
								/>
							</button>
						</AppTooltip>
					</nav>
				</aside>
			</div>
			<ConfirmModal
				:open="confirmOpen"
				message="Er du sikker på du vil logge ud?"
				accept-text="Log ud"
				title="Bekræft log ud"
				:danger="true"
				@confirm="onClickLogout"
				@cancel="confirmOpen = false"
				@close="confirmOpen = false"
			/>
		</div>
		<main class="flex flex-col flex-1 lg:px-4 min-h-0 overflow-y-auto">
			<RouterView />
		</main>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import SearchhistoryIcon from '@/assets/icons/coco/duotone/Message - 3.svg?component';
import SearchIcon from '@/assets/icons/coco/duotone/Search.svg?component';
import SettingsIcon from '@/assets/icons/coco/duotone/Setting.svg?component';
import FavoritesIcon from '@/assets/icons/coco/duotone/Star.svg?component';
import LogOutIcon from '@/assets/icons/coco/line/Turn off.svg?component';
import FlashcardsIcon from '@/assets/icons/custom/flashcard-1.svg?component';
import AppTooltip from '@/components/ui/AppTooltip.vue';
import MobileMenuDropdown from '@/components/ui/MobileMenuDropdown.vue';
import ConfirmModal from '@/components/ui/modals/ConfirmModal.vue';
import { useUserStore } from '@/stores/user';
import { isLg, isMobile } from '@/utils/generalUtils';

const confirmOpen = ref(false);
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();

function onClickLogout() {
	userStore.logout().then(() => router.push({ name: 'home' }));
}

const homeLink = computed(() => {
	// carry over language parameters if any
	// omit q to land on page without results
	const keep = ['s', 't'];
	const filtered: Record<string, string> = {};

	keep.forEach(key => {
		const v = route.query[key];
		if (typeof v === 'string' && v.length > 0) {
			filtered[key] = v;
		}
	});

	return {
		name: 'search',
		query: filtered,
	};
});

const menuItems = computed(() => [
	{ label: 'SØG', icon: SearchIcon, to: homeLink.value, class: 'icon-blue-soft' },
	{ label: 'ORDLISTER', icon: FavoritesIcon, to: { name: 'entry-lists' }, class: 'icon-yellow' },
	{ label: 'FLASHCARDS', icon: FlashcardsIcon, to: { name: 'flashcards' }, class: 'icon-green-soft' },
	{ label: 'HISTORIK', icon: SearchhistoryIcon, to: { name: 'search-history' }, class: 'icon-purple-soft' },
	{ label: 'INDSTILLINGER', icon: SettingsIcon, to: { name: 'settings' }, class: 'icon-gray' },
]);
</script>

<style scoped>
.icons-container {
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	width: auto;
	padding: 1rem;
	--hover-search-translate-x: 60%;
	--hover-img-translate-x: -20%;
}
@media (min-width: 1030px) {
	.icons-container {
		--hover-search-translate-x: 50%;
		--hover-img-translate-x: -30%;
	}
}

.icons-container > img {
	display: block;
}

.search-icon {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -55%);
	opacity: 0;
}
.icons-container > img,
.search-icon {
	transition:
		transform 0.3s ease,
		opacity 0.3s ease;
}

.icons-container:hover .search-icon {
	opacity: 1;
	transform: translate(var(--hover-search-translate-x), -55%);
}

.icons-container:hover > img {
	transform: translateX(var(--hover-img-translate-x));
}

@media (min-width: 1030px) {
	.icons-container:hover .search-icon {
		opacity: 1;
		transform: translate(var(--hover-search-translate-x), -55%);
	}

	.icons-container:hover > img {
		transform: translateX(var(--hover-img-translate-x));
	}
}
</style>
